import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';

const PharmacyQuickActions = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('expiry'); 
  const [stats, setStats] = useState({
    sales: 0, purchases: 0, dueAmount: 0, customers: 0
  });
  const [alerts, setAlerts] = useState({
    lowStock: 0, expiringSoon: 0, pendingDues: 0
  });
  
  const [loading, setLoading] = useState(true);
  
  // Naye States for Dynamic Expiry Filter
  const [allExpiryData, setAllExpiryData] = useState([]);
  const [expiryDaysFilter, setExpiryDaysFilter] = useState(60); // Default 60 days
  const [expiryLoading, setExpiryLoading] = useState(false);

  useEffect(() => {
    fetchQuickStats();
    fetchExpiryData(); 
  }, []);

  const fetchQuickStats = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/PharmacyStats/quick-summary`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats({
          sales: data.sales || 0,
          purchases: data.purchases || 0,
          dueAmount: data.dueAmount || 0,
          customers: data.customers || 0
        });
        setAlerts({
          lowStock: data.lowStock || 0,
          expiringSoon: data.expiringSoon || 0,
          pendingDues: data.pendingDues || 0
        });
      }
    } catch (error) {
      console.error("Failed to fetch quick stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiryData = async () => {
    try {
      setExpiryLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/ExpiryReports/near-expiry`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Sirf raw data save kar rahe hain
        setAllExpiryData(data);
      }
    } catch (error) {
      console.error("Failed to fetch expiry data:", error);
    } finally {
      setExpiryLoading(false);
    }
  };

  // Real-time filtering based on dropdown selection
  const filteredExpiryList = allExpiryData.filter(item => {
    const daysLeft = item.daysRemaining ?? item.DaysRemaining;
    return daysLeft !== undefined && daysLeft <= expiryDaysFilter;
  }).sort((a, b) => (a.daysRemaining ?? a.DaysRemaining) - (b.daysRemaining ?? b.DaysRemaining));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', minimumFractionDigits: 0
    }).format(amount);
  };

  const primaryActions = [
    { name: 'POS / New Sale', path: '/pharmacy/sale', icon: 'bi-cart-check', shortcut: 'Ctrl+S', color: 'sale' },
    { name: 'New Purchase', path: '/pharmacy/purchase', icon: 'bi-bag-plus', shortcut: 'Ctrl+P', color: 'purchase' },
    { name: 'Add Medicine', path: '/pharmacy/medicine', icon: 'bi-plus-square', shortcut: 'Ctrl+M', color: 'med' },
  ];

  const secondaryActions = [
    { name: 'Sales Register', path: '/pharmacy/sale-search', icon: 'bi-receipt', shortcut: 'Ctrl+Shift+S', color: 'reg' },
    { name: 'Stock Out', path: '/pharmacy/stock-out', icon: 'bi-box-arrow-right', shortcut: 'Ctrl+O', color: 'stock' },
    { name: 'Debit Voucher', path: '/pharmacy/debit-voucher', icon: 'bi-file-earmark-minus', shortcut: 'Ctrl+B', color: 'debit' },
    { name: 'Sale Return', path: '/pharmacy/return', icon: 'bi-arrow-return-left', shortcut: 'Ctrl+R', color: 'return' },
    { name: 'Due Collection', path: '/pharmacy/MedicineDuePaymentCollect', icon: 'bi-cash-coin', shortcut: 'Ctrl+D', color: 'due' },
    { name: 'Search Medicine', path: '/pharmacy/medicine-search', icon: 'bi-search', shortcut: 'Ctrl+K', color: 'search' },
  ];

  const reportShortcuts = [
    { name: 'Dashboard', path: '/pharmacy/dashboard-report', icon: 'bi-speedometer2' },
    { name: 'Sales Report', path: '/pharmacy/sales-reports', icon: 'bi-graph-up-arrow' },
    { name: 'Inventory', path: '/pharmacy/inventory-reports', icon: 'bi-box-seam' },
    { name: 'GSTR-1', path: '/pharmacy/GSTR1Report', icon: 'bi-file-earmark-spreadsheet' },
  ];

  return (
    <div className="qa-container">
      <div className="qa-header">
        <div className="qa-tabs">
          <button 
            className={`qa-tab ${activeTab === 'expiry' ? 'active' : ''}`}
            onClick={() => setActiveTab('expiry')}
          >
            <i className="bi bi-hourglass-split"></i> Expiry
            {filteredExpiryList.length > 0 && (
              <span className="tab-badge" style={{background: '#dc2626'}}>{filteredExpiryList.length}</span>
            )}
          </button>
          <button 
            className={`qa-tab ${activeTab === 'actions' ? 'active' : ''}`}
            onClick={() => setActiveTab('actions')}
          >
            <i className="bi bi-lightning-charge-fill"></i> Actions
          </button>
          <button 
            className={`qa-tab ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <i className="bi bi-bar-chart-fill"></i> Stats
          </button>
          <button 
            className={`qa-tab ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('alerts')}
          >
            <i className="bi bi-bell-fill"></i> Alerts
            {(alerts.lowStock + alerts.expiringSoon) > 0 && (
              <span className="tab-badge">{alerts.lowStock + alerts.expiringSoon}</span>
            )}
          </button>
        </div>
      </div>

      <div className="qa-body">
        
        {activeTab === 'expiry' && (
          <>
            <div className="qa-section-label" style={{ alignItems: 'center' }}>
              <span>Expiring In:</span>
              
              <select 
                className="expiry-filter-select"
                value={expiryDaysFilter}
                onChange={(e) => setExpiryDaysFilter(Number(e.target.value))}
              >
                <option value={30}>Next 30 Days</option>
                <option value={60}>Next 2 Months</option>
                <option value={90}>Next 3 Months</option>
                <option value={180}>Next 6 Months</option>
              </select>

              {expiryLoading ? (
                <div className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px', color: '#dc2626', marginLeft: 'auto'}}></div>
              ) : (
                <i className="bi bi-arrow-clockwise refresh-icon" style={{marginLeft: 'auto'}} onClick={fetchExpiryData}></i>
              )}
            </div>

            <div className="expiry-list-container mt-2">
              {!expiryLoading && filteredExpiryList.length === 0 ? (
                <div className="empty-expiry">
                   <i className="bi bi-check-circle-fill"></i>
                   <span>All Good! No items expiring in selected period.</span>
                </div>
              ) : (
                <div className="expiry-items">
                  {filteredExpiryList.map((item, index) => {
                    const daysLeft = item.daysRemaining ?? item.DaysRemaining;
                    const isCritical = daysLeft <= 30;
                    return (
                      <div 
                        key={index} 
                        className={`expiry-card ${isCritical ? 'critical' : 'warning'}`}
                        onClick={() => navigate('/pharmacy/expiry-reports')}
                      >
                        <div className="ec-left">
                          <div className="ec-name">{item.medicine || item.Medicine}</div>
                          <div className="ec-batch">Batch: {item.batchNo || item.BatchNo} | Stock: {item.balancePieces || item.BalancePieces} Pcs</div>
                        </div>
                        <div className="ec-right">
                           <div className="ec-days">{daysLeft} Days</div>
                           <div className="ec-date">{new Date(item.expDate || item.ExpDate).toLocaleDateString('en-IN', {month: 'short', year: 'numeric'})}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {filteredExpiryList.length > 0 && (
              <button className="view-full-report-btn mt-3" onClick={() => navigate('/pharmacy/expiry-reports')}>
                View Full Expiry Report <i className="bi bi-arrow-right"></i>
              </button>
            )}
          </>
        )}

        {activeTab === 'actions' && (
          <>
            <div className="qa-section-label">
              <span>Primary Actions</span>
              <i className="bi bi-star-fill" style={{color: '#f59e0b', fontSize: '10px'}}></i>
            </div>
            <div className="qa-group">
              {primaryActions.map((action, i) => (
                <button 
                  key={i}
                  className={`erp-action-card ${action.color}-card primary-card`} 
                  onClick={() => navigate(action.path)}
                >
                  <div className="icon-wrapper"><i className={`bi ${action.icon}`}></i></div>
                  <span className="card-text">{action.name}</span>
                  <span className="qa-key">{action.shortcut}</span>
                </button>
              ))}
            </div>

            <div className="qa-section-label mt-3">
              <span>More Actions</span>
            </div>
            <div className="qa-group compact">
              {secondaryActions.map((action, i) => (
                <button 
                  key={i}
                  className={`erp-action-card ${action.color}-card`} 
                  onClick={() => navigate(action.path)}
                >
                  <div className="icon-wrapper"><i className={`bi ${action.icon}`}></i></div>
                  <span className="card-text">{action.name}</span>
                  <span className="qa-key">{action.shortcut}</span>
                </button>
              ))}
            </div>

            <div className="qa-section-label mt-3">
              <span>Quick Reports</span>
            </div>
            <div className="report-grid">
              {reportShortcuts.map((rep, i) => (
                <button 
                  key={i}
                  className="report-tile" 
                  onClick={() => navigate(rep.path)}
                  title={rep.name}
                >
                  <i className={`bi ${rep.icon}`}></i>
                  <span>{rep.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {activeTab === 'stats' && (
          <>
            <div className="qa-section-label">
              <span>Today's Summary</span>
              {loading ? (
                <div className="spinner-border spinner-border-sm" style={{width: '12px', height: '12px', color: '#059669'}}></div>
              ) : (
                <i className="bi bi-arrow-clockwise refresh-icon" onClick={fetchQuickStats}></i>
              )}
            </div>
            <div className="erp-stats-grid">
              <div className="stat-box box-success">
                <i className="bi bi-graph-up-arrow stat-bg-icon"></i>
                <div className="stat-label">Sales Today</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.sales)}</div>
              </div>
              <div className="stat-box box-danger">
                <i className="bi bi-cart-dash stat-bg-icon"></i>
                <div className="stat-label">Purchases</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.purchases)}</div>
              </div>
              <div className="stat-box box-warning">
                <i className="bi bi-cash-stack stat-bg-icon"></i>
                <div className="stat-label">Pending Dues</div>
                <div className="stat-value">{loading ? '...' : formatCurrency(stats.dueAmount)}</div>
              </div>
              <div className="stat-box box-info">
                <i className="bi bi-people-fill stat-bg-icon"></i>
                <div className="stat-label">Customers</div>
                <div className="stat-value">{loading ? '...' : stats.customers}</div>
              </div>
            </div>

            <div className="qa-section-label mt-3">
              <span>Performance</span>
            </div>
            <div className="perf-card">
              <div className="perf-row">
                <span className="perf-label">Profit Margin</span>
                <span className="perf-value text-success">+12.5%</span>
              </div>
              <div className="perf-bar">
                <div className="perf-fill" style={{width: '65%', background: '#059669'}}></div>
              </div>
              <div className="perf-row mt-2">
                <span className="perf-label">Target Achievement</span>
                <span className="perf-value text-accent">78%</span>
              </div>
              <div className="perf-bar">
                <div className="perf-fill" style={{width: '78%', background: '#0d9488'}}></div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'alerts' && (
          <>
            <div className="qa-section-label">
              <span>Important Alerts</span>
              <i className="bi bi-arrow-clockwise refresh-icon" onClick={fetchQuickStats}></i>
            </div>
            <div className="alert-list">
              <div className="alert-item alert-danger" onClick={() => navigate('/pharmacy/low-stock')}>
                <div className="alert-icon"><i className="bi bi-exclamation-octagon-fill"></i></div>
                <div className="alert-meta">
                  <div className="alert-title">Low Stock Items</div>
                  <div className="alert-desc">Medicines below reorder level</div>
                </div>
                <div className="alert-count">{alerts.lowStock}</div>
              </div>

              <div className="alert-item alert-warning" onClick={() => navigate('/pharmacy/expiry-tracker')}>
                <div className="alert-icon"><i className="bi bi-calendar-x-fill"></i></div>
                <div className="alert-meta">
                  <div className="alert-title">Expiring Soon</div>
                  <div className="alert-desc">Within next 30 days</div>
                </div>
                <div className="alert-count">{alerts.expiringSoon}</div>
              </div>

              <div className="alert-item alert-info" onClick={() => navigate('/pharmacy/MediVendorDueReport')}>
                <div className="alert-icon"><i className="bi bi-cash-coin"></i></div>
                <div className="alert-meta">
                  <div className="alert-title">Pending Dues</div>
                  <div className="alert-desc">Vendor payments due</div>
                </div>
                <div className="alert-count">{alerts.pendingDues}</div>
              </div>
            </div>

            <div className="qa-section-label mt-3">
              <span>Quick Tasks</span>
            </div>
            <div className="task-list">
              <div className="task-item" onClick={() => navigate('/pharmacy/stock-audit')}>
                <i className="bi bi-clipboard-check"></i>
                <span>Run Stock Audit</span>
                <i className="bi bi-chevron-right arrow-r"></i>
              </div>
              <div className="task-item" onClick={() => navigate('/pharmacy/backup')}>
                <i className="bi bi-cloud-arrow-up"></i>
                <span>Backup Data</span>
                <i className="bi bi-chevron-right arrow-r"></i>
              </div>
              <div className="task-item" onClick={() => navigate('/pharmacy/GSTR1Report')}>
                <i className="bi bi-file-earmark-spreadsheet"></i>
                <span>Generate GST Report</span>
                <i className="bi bi-chevron-right arrow-r"></i>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .qa-container {
          --qa-accent: #059669;
          --qa-accent-dark: #047857;
          --qa-accent-soft: #ecfdf5;
          --qa-accent-border: #a7f3d0;

          display: flex; flex-direction: column;
          height: 100%; background: #ffffff;
        }

        .qa-header {
          border-bottom: 1px solid #f1f5f9;
          background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
        }

        .qa-tabs {
          display: flex; padding: 8px 8px 0 8px;
          gap: 2px;
        }

        .qa-tab {
          flex: 1; background: transparent;
          border: none; padding: 9px 4px;
          font-size: 11px; font-weight: 700;
          color: #64748b; cursor: pointer;
          border-radius: 7px 7px 0 0;
          display: flex; align-items: center;
          justify-content: center; gap: 4px;
          position: relative; transition: all 0.2s;
        }
        .qa-tab i { font-size: 11px; }
        .qa-tab:hover { background: #f8fafc; color: #0f172a; }
        .qa-tab.active {
          background: var(--qa-accent-soft);
          color: var(--qa-accent-dark);
          border-bottom: 2px solid var(--qa-accent);
        }

        .tab-badge {
          position: absolute; top: 0px; right: 2px;
          background: #ef4444; color: white;
          font-size: 9px; font-weight: 700;
          min-width: 14px; height: 14px;
          border-radius: 8px; padding: 0 3px;
          display: flex; align-items: center;
          justify-content: center;
        }

        .qa-body {
          padding: 12px; overflow-y: auto; flex: 1;
        }
        .qa-body::-webkit-scrollbar { width: 5px; }
        .qa-body::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 10px;
        }

        .qa-section-label {
          font-size: 10px; font-weight: 800;
          color: #94a3b8; text-transform: uppercase;
          letter-spacing: 0.6px; margin-bottom: 8px;
          display: flex; justify-content: space-between;
          align-items: center;
        }
        .refresh-icon {
          color: var(--qa-accent); cursor: pointer;
          font-size: 12px; transition: transform 0.3s;
        }
        .refresh-icon:hover { transform: rotate(180deg); }

        .expiry-filter-select {
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 5px;
          color: #0f172a;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 6px;
          outline: none;
          cursor: pointer;
          margin-left: 8px;
        }
        .expiry-filter-select:focus {
          border-color: var(--qa-accent);
          box-shadow: 0 0 0 2px var(--qa-accent-soft);
        }

        .expiry-list-container {
          display: flex; flex-direction: column; gap: 8px;
        }
        .empty-expiry {
          text-align: center; padding: 30px 15px;
          background: #f0fdf4; border: 1px dashed #bbf7d0;
          border-radius: 8px; color: #16a34a;
          display: flex; flex-direction: column; align-items: center; gap: 8px;
        }
        .empty-expiry i { font-size: 24px; }
        .empty-expiry span { font-size: 12px; font-weight: 600; }
        
        .expiry-items {
          display: flex; flex-direction: column; gap: 8px;
        }
        .expiry-card {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 12px; border-radius: 8px; border: 1px solid transparent;
          cursor: pointer; transition: transform 0.2s, box-shadow 0.2s;
        }
        .expiry-card:hover { transform: translateY(-1px); box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
        .expiry-card.critical { background: #fef2f2; border-color: #fecaca; }
        .expiry-card.warning { background: #fffbeb; border-color: #fde68a; }

        .ec-left { display: flex; flex-direction: column; gap: 2px; flex: 1; }
        .ec-name { font-size: 13px; font-weight: 700; color: #0f172a; }
        .ec-batch { font-size: 10px; font-weight: 600; color: #64748b; }

        .ec-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
        .ec-days { 
          font-size: 12px; font-weight: 800; 
          padding: 2px 6px; border-radius: 4px;
        }
        .critical .ec-days { background: #dc2626; color: white; animation: pulse 2s infinite; }
        .warning .ec-days { background: #f59e0b; color: white; }
        .ec-date { font-size: 9px; font-weight: 700; color: #64748b; text-transform: uppercase; }

        .view-full-report-btn {
          width: 100%; padding: 10px; border-radius: 8px;
          background: #f1f5f9; border: 1px solid #e2e8f0;
          color: #475569; font-weight: 700; font-size: 12px;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.2s;
        }
        .view-full-report-btn:hover { background: #e2e8f0; color: #0f172a; }

        .qa-group {
          display: flex; flex-direction: column; gap: 6px;
        }
        .qa-group.compact { gap: 4px; }
        .qa-group.compact .erp-action-card { padding: 6px 10px; }
        .qa-group.compact .icon-wrapper { width: 28px; height: 28px; font-size: 12px; }

        .erp-action-card {
          display: flex; align-items: center;
          width: 100%; padding: 8px 12px;
          border: 1px solid #f1f5f9; border-radius: 9px;
          background: #ffffff;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          cursor: pointer; transition: all 0.2s ease;
          text-align: left;
        }
        .primary-card { padding: 10px 12px; }
        .primary-card .icon-wrapper { width: 36px; height: 36px; font-size: 16px; }

        .erp-action-card:hover {
          transform: translateX(-2px);
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
          border-color: #e2e8f0;
        }

        .icon-wrapper {
          width: 30px; height: 30px;
          display: flex; align-items: center;
          justify-content: center; border-radius: 8px;
          margin-right: 10px; font-size: 13px;
          transition: all 0.2s;
        }

        .card-text {
          font-size: 12.5px; font-weight: 600;
          color: #475569; flex: 1;
        }

        .qa-key {
          font-size: 10px; background: #f1f5f9;
          padding: 3px 6px; border-radius: 5px;
          color: #64748b; font-family: 'Consolas', monospace;
          font-weight: 600; border: 1px solid #e2e8f0;
          white-space: nowrap;
        }

        .sale-card .icon-wrapper { background: #dcfce7; color: #16a34a; }
        .sale-card:hover { background: #f0fdf4; border-color: #bbf7d0; }
        .purchase-card .icon-wrapper { background: #dbeafe; color: #2563eb; }
        .purchase-card:hover { background: #eff6ff; border-color: #bfdbfe; }
        .med-card .icon-wrapper { background: #fee2e2; color: #dc2626; }
        .med-card:hover { background: #fef2f2; border-color: #fecaca; }
        .reg-card .icon-wrapper { background: #f3e8ff; color: #9333ea; }
        .reg-card:hover { background: #faf5ff; border-color: #e9d5ff; }
        .stock-card .icon-wrapper { background: #ffedd5; color: #ea580c; }
        .stock-card:hover { background: #fff7ed; border-color: #fed7aa; }
        .debit-card .icon-wrapper { background: #ccfbf1; color: #0d9488; }
        .debit-card:hover { background: #f0fdfa; border-color: #99f6e4; }
        .return-card .icon-wrapper { background: #fce7f3; color: #db2777; }
        .return-card:hover { background: #fdf2f8; border-color: #fbcfe8; }
        .due-card .icon-wrapper { background: #fef3c7; color: #d97706; }
        .due-card:hover { background: #fffbeb; border-color: #fde68a; }
        .search-card .icon-wrapper { background: #e0e7ff; color: #4f46e5; }
        .search-card:hover { background: #eef2ff; border-color: #c7d2fe; }

        .report-grid {
          display: grid; grid-template-columns: 1fr 1fr;
          gap: 6px;
        }
        .report-tile {
          background: #f8fafc; border: 1px solid #f1f5f9;
          border-radius: 9px; padding: 10px 6px;
          display: flex; flex-direction: column;
          align-items: center; gap: 4px;
          cursor: pointer; transition: all 0.2s;
        }
        .report-tile i { font-size: 16px; color: var(--qa-accent); }
        .report-tile span { font-size: 10px; font-weight: 600; color: #475569; }
        .report-tile:hover {
          background: var(--qa-accent-soft); border-color: var(--qa-accent-border);
          transform: translateY(-1px);
        }

        .erp-stats-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
        }
        .stat-box {
          background: #ffffff; border-radius: 9px;
          padding: 10px; position: relative;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          border: 1px solid #e2e8f0;
        }
        .stat-bg-icon {
          position: absolute; right: -5px; bottom: -10px;
          font-size: 38px; opacity: 0.05;
        }
        .stat-label {
          font-size: 10px; color: #64748b;
          font-weight: 700; margin-bottom: 3px;
          text-transform: uppercase; letter-spacing: 0.3px;
        }
        .stat-value { font-size: 14px; font-weight: 800; }

        .box-success .stat-value { color: #059669; }
        .box-danger .stat-value { color: #dc2626; }
        .box-warning .stat-value { color: #d97706; }
        .box-info .stat-value { color: #2563eb; }

        .perf-card {
          background: #f8fafc; border: 1px solid #f1f5f9;
          border-radius: 9px; padding: 12px;
        }
        .perf-row {
          display: flex; justify-content: space-between;
          font-size: 11px; font-weight: 600;
          margin-bottom: 4px;
        }
        .perf-label { color: #64748b; }
        .perf-value { font-weight: 700; }
        .text-success { color: #059669; }
        .text-accent { color: #0d9488; }
        .perf-bar {
          height: 6px; background: #e2e8f0;
          border-radius: 10px; overflow: hidden;
        }
        .perf-fill {
          height: 100%; border-radius: 10px;
          transition: width 1s ease;
        }

        .alert-list {
          display: flex; flex-direction: column; gap: 6px;
        }
        .alert-item {
          display: flex; align-items: center; gap: 10px;
          padding: 10px; border-radius: 9px;
          cursor: pointer; transition: all 0.2s;
          border: 1px solid transparent;
        }
        .alert-danger { background: #fef2f2; border-color: #fecaca; }
        .alert-warning { background: #fffbeb; border-color: #fde68a; }
        .alert-info { background: #eff6ff; border-color: #bfdbfe; }
        .alert-item:hover { transform: translateX(-2px); }

        .alert-icon {
          width: 32px; height: 32px;
          border-radius: 9px; display: flex;
          align-items: center; justify-content: center;
          font-size: 14px; background: white;
        }
        .alert-danger .alert-icon { color: #dc2626; }
        .alert-warning .alert-icon { color: #d97706; }
        .alert-info .alert-icon { color: #2563eb; }

        .alert-meta { flex: 1; }
        .alert-title {
          font-size: 12px; font-weight: 700; color: #0f172a;
        }
        .alert-desc {
          font-size: 10px; color: #64748b; margin-top: 1px;
        }
        .alert-count {
          font-size: 16px; font-weight: 800;
          min-width: 32px; text-align: center;
        }
        .alert-danger .alert-count { color: #dc2626; }
        .alert-warning .alert-count { color: #d97706; }
        .alert-info .alert-count { color: #2563eb; }

        .task-list {
          display: flex; flex-direction: column; gap: 4px;
        }
        .task-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px; border-radius: 7px;
          cursor: pointer; transition: all 0.15s;
          font-size: 12px; font-weight: 600; color: #475569;
        }
        .task-item:hover { background: var(--qa-accent-soft); color: var(--qa-accent-dark); }
        .task-item > i:first-child { color: var(--qa-accent); font-size: 14px; }
        .task-item span { flex: 1; }
        .arrow-r { font-size: 11px !important; opacity: 0.5; }

        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          70% { box-shadow: 0 0 0 4px rgba(220, 38, 38, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
      `}</style>
    </div>
  );
};

export default PharmacyQuickActions;